import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RAIL_MAX_PX,
  RAIL_MIN_PX,
  useUiStore,
} from '@/stores/ui-store';

/**
 * Drag-resize behavior for the right rail. Pointer-down on the leading
 * edge of the rail starts a drag.
 *
 * During the drag we **do not** write to the persisted Zustand store on
 * every mousemove — that thrashes localStorage and forces a re-render of
 * every store subscriber. Instead we write the in-flight width into a CSS
 * custom property (`--rail-w-eff`) on `<html>` directly so the grid track
 * reflows in real time, and only commit the final width to the store on
 * mouseup. After commit, `applyUiToDom` keeps the CSS variable in sync.
 *
 * Body cursor / userSelect overrides live for the lifetime of the drag.
 * The effect's cleanup function resets them so we never leak the
 * `col-resize` cursor or the `userSelect: none` rule across mid-drag
 * unmounts (e.g. route change while the user is dragging).
 *
 * Width is clamped to [RAIL_MIN_PX, RAIL_MAX_PX]; the store re-clamps
 * defensively on commit.
 */
export function useResizableRail() {
  const railWidth = useUiStore((s) => s.railWidth);
  const setRailWidth = useUiStore((s) => s.setRailWidth);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const transientWidthRef = useRef<number>(railWidth);
  const [isDragging, setIsDragging] = useState(false);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startW: railWidth };
      transientWidthRef.current = railWidth;
      setIsDragging(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [railWidth],
  );

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (ev: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Rail lives on the trailing edge → moving the cursor to the
      // start (leading) of the page should *grow* the rail. Subtract
      // the delta so the visual feels right.
      const dx = drag.startX - ev.clientX;
      const projected = Math.max(
        RAIL_MIN_PX,
        Math.min(RAIL_MAX_PX, drag.startW + dx),
      );
      transientWidthRef.current = projected;
      // CSS-var write is cheap; the grid track reflows immediately
      // without any React re-render or persisted-store churn.
      document.documentElement.style.setProperty(
        '--rail-w-eff',
        `${projected}px`,
      );
    };
    const onUp = () => {
      // Commit the final width to the store — `applyUiToDom` will keep
      // the CSS variable in sync from here on.
      setRailWidth(transientWidthRef.current);
      dragRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Always clear cursor/userSelect overrides on cleanup — covers
      // both the normal mouseup teardown and the mid-drag unmount case
      // (e.g. route change while the user is still dragging).
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Mid-drag unmount: dragRef is still set (mouseup never fired),
      // which means we wrote a transient `--rail-w-eff` that no longer
      // matches the persisted store value. Restore the CSS var to the
      // store width so the next mount renders the rail at the correct
      // size instead of the abandoned drag's width.
      if (dragRef.current) {
        document.documentElement.style.setProperty(
          '--rail-w-eff',
          `${railWidth}px`,
        );
        dragRef.current = null;
      }
    };
  }, [isDragging, railWidth, setRailWidth]);

  return { onMouseDown, isDragging };
}
